// CrudeMode.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ModeId } from '../ModeLanding';
import ModeSelector from '../ModeSelector';
import NewDatabaseFilterPanel from '../NewDatabaseFilterPanel';
import NewDatabaseGraphPanel from '../NewDatabaseGraphPanel';
import NewDatabaseGraphToggle from '../NewDatabaseGraphToggle';
import NewDatabaseRowModal from '../NewDatabaseRowModal';
import SQLTable from '../../../components/sql_table/tableMain';
import TabToggle from '../../../components/common/tab_toggle/tabToggle';
import { useApiData } from '../../../contexts/apiService';
import { useMonthNavigation } from '../../../contexts/monthNavigationContext';
import { useDataManager } from '../../../contexts/dataManager';
import { useUserAccess, USER_ROLE_LEVEL } from '../../../contexts/userAccess';
import { getUniqueDropdownOptions } from '../../../helper_functions';
import { getColConfigs } from '../tableConfig';
import { Filter } from 'lucide-react';

const CRUDE_TABS = [
    { label: 'Main',     value: 'crude_main' },
    { label: 'Pickup',   value: 'crude_main_pickup' },
    { label: 'Delivery', value: 'crude_main_delivery' },
];

interface CrudeModeProps {
    initialMonth?: string;
    initialRid?: any;
    initialMonthForRid?: string;
    onChangeMode: (mode: ModeId) => void;
    onBackToOverview: () => void;
}

const CrudeMode: React.FC<CrudeModeProps> = ({
    initialMonth,
    initialRid,
    initialMonthForRid,
    onChangeMode,
    onBackToOverview,
}) => {
    const { apiData } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();
    const { setMonthSelection, getMonthSelection } = useMonthNavigation();
    const { userRole = 'viewer', hasAdminAccess, hasAccAccess } = useUserAccess();
    const SCHEDULER = USER_ROLE_LEVEL['scheduler'];
    const showTable = Number(USER_ROLE_LEVEL[userRole]) >= SCHEDULER;

    const [activeTable, setActiveTable] = useState<string>('crude_main');
    const [initialFilter, setInitialFilter] = useState<{ column: string; value: any } | undefined>(undefined);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [showGraph, setShowGraph] = useState(false);
    const [graphHeight, setGraphHeight] = useState(60);
    const [isDragging, setIsDragging] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
    const [selectedRowId, setSelectedRowId] = useState<number | null>(null);
    const [showRowModal, setShowRowModal] = useState(false);
    const [activeFilters, setActiveFilters] = useState<string[]>([]);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Set initial month on mount
    useEffect(() => {
        if (initialMonth) {
            setMonthSelection('crude_main', initialMonth);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Deep-link from schedule table side panel
    useEffect(() => {
        const loadDataAndApplyFilter = async () => {
            if (initialRid && initialMonthForRid) {
                setMonthSelection('crude_main_pickup', initialMonthForRid);
                setActiveTable('crude_main_pickup');
                if (!hasTableData('crude_main_pickup', initialMonthForRid)) {
                    await requestTableData('crude_main_pickup', initialMonthForRid);
                }
                setInitialFilter({ column: 'rid', value: initialRid });
            }
        };
        loadDataAndApplyFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRid, initialMonthForRid]);

    const handleTabChange = (tab: string) => {
        const currentMonth = getMonthSelection(activeTable);
        if (currentMonth) setMonthSelection(tab, currentMonth);
        setActiveTable(tab);
    };

    const handleRowClick = useCallback((rowData: any) => {
        setSelectedRowData(rowData);
        setSelectedRowId(rowData.id);
        //just console log for now to verify correct data is being passed, will implement modal with more useful info later
        console.log('Row clicked:', rowData);
        //setShowRowModal(true);
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleCloseFilterPanel = useCallback(() => setShowFilterPanel(false), []);
    const handleCloseRowModal = useCallback(() => {
        setShowRowModal(false);
        setSelectedRowId(null);
    }, []);

    // Drag-to-resize
    useEffect(() => {
        let resizeTimeout: ReturnType<typeof setTimeout>;

        const handleMouseMove = (e: MouseEvent) => {
            if (!isDragging) return;
            const mainContent = document.querySelector('.ticketData-mainContent') as HTMLElement;
            if (!mainContent) return;
            const rect = mainContent.getBoundingClientRect();
            const offsetY = e.clientY - rect.top;
            const newHeight = ((rect.height - offsetY) / rect.height) * 100;
            const constrainedHeight = Math.min(Math.max(newHeight, 20), 80);
            setGraphHeight(constrainedHeight);
            setIsTransitioning(true);
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
                setIsTransitioning(false);
            }, 150);
        };

        const handleMouseUp = () => {
            setIsDragging(false);
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
                setIsTransitioning(false);
            }, 100);
        };

        if (isDragging) {
            setIsTransitioning(true);
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'ns-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            clearTimeout(resizeTimeout);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging]);

    // Trigger chart redraw when panels open/close
    useEffect(() => {
        if (showGraph) {
            setIsTransitioning(true);
            const timer = setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
                setIsTransitioning(false);
            }, 350);
            return () => clearTimeout(timer);
        }
    }, [showRowModal, showFilterPanel, showGraph]);

    const producerOptions = useMemo(() => {
        if (!apiData || !activeTable) return [];
        const rawData = (apiData as any)[activeTable] || [];
        return getUniqueDropdownOptions({ tableData: rawData, valueColumn: 'producer', labelColumn: 'producer' });
    }, [apiData, activeTable]);

    const leaseOptions = useMemo(() => {
        if (!apiData || !activeTable) return [];
        const rawData = (apiData as any)[activeTable] || [];
        return getUniqueDropdownOptions({ tableData: rawData, valueColumn: 'lease', labelColumn: 'lease' });
    }, [apiData, activeTable]);

    const destinationOptions = useMemo(() => {
        if (!apiData || !activeTable) return [];
        const rawData = (apiData as any)[activeTable] || [];
        return getUniqueDropdownOptions({ tableData: rawData, valueColumn: 'destination', labelColumn: 'destination' });
    }, [apiData, activeTable]);

    const COL_CONFIGS = useMemo(
        () => getColConfigs(activeTable, producerOptions, leaseOptions, destinationOptions),
        [activeTable, producerOptions, leaseOptions, destinationOptions]
    );

    const getTableData = useMemo(() => {
        if (!apiData || !activeTable) return [];
        const rawData = (apiData as any)[activeTable] || [];

        if (activeTable === 'crude_main') {
            return rawData.map((row: any) => {
                if (row.total_calculated_trucking != null && row.total_calculated_trucking !== '') {
                    const parseNumber = (value: any): number => {
                        if (value === null || value === undefined || value === '') return 0;
                        const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
                        return isNaN(parsed) ? 0 : parsed;
                    };
                    const originalNetProfit = parseNumber(row.net_profit);
                    const totalTrucking = parseNumber(row.total_trucking);
                    const totalCalculatedTrucking = parseNumber(row.total_calculated_trucking);
                    const recalculatedNetProfit = originalNetProfit + totalTrucking - totalCalculatedTrucking;
                    return {
                        ...row,
                        net_profit: Math.round(recalculatedNetProfit * 100) / 100,
                        total_trucking: totalCalculatedTrucking,
                    };
                }
                return row;
            });
        }

        return rawData;
    }, [apiData, activeTable]);

    const filteredTableData = useMemo(() => {
        let data = getTableData;
        activeFilters.forEach(filter => {
            switch (filter) {
                case 'short_loads':
                    data = data.filter((row: any) => (Number(row.gross_barrels) || 0) < 160);
                    break;
                case 'negative_profit':
                    data = data.filter((row: any) => (Number(row.net_profit) || 0) < 0);
                    break;
                case 'high_bsw':
                    data = data.filter((row: any) => (Number(row.bsw) || 0) > 1);
                    break;
            }
        });
        return data;
    }, [getTableData, activeFilters]);

    return (
        <>
            <div className="ticketData-topBar">
                <ModeSelector
                    activeMode="crude"
                    onChangeMode={onChangeMode}
                    onBackToOverview={onBackToOverview}
                />

                <div className="ticketData-tabContainer">
                    <TabToggle
                        tabOptions={CRUDE_TABS.map(t => ({ label: t.label, value: t.value }))}
                        activeTab={activeTable}
                        onChange={handleTabChange}
                    />
                </div>

                <div className="newdb-filter-button-wrapper">
                    <button
                        className={`newdb-filter-toggle-button ${activeFilters.length > 0 && !showFilterPanel ? 'newdb-filter-toggle-button--animated' : ''}`}
                        onClick={() => setShowFilterPanel(!showFilterPanel)}
                        title="Toggle Filters"
                    >
                        <Filter size={18} />
                        <span>Quick Filters</span>
                    </button>
                    {activeFilters.length > 0 && !showFilterPanel && (
                        <svg className="newdb-filter-animated-border" width="100%" height="100%">
                            <rect
                                x="2" y="2"
                                width="calc(100% - 4px)" height="calc(100% - 4px)"
                                rx="8" ry="8"
                                fill="none"
                                stroke="#ffa500"
                                strokeWidth="2"
                                strokeDasharray="5 5"
                                strokeDashoffset="0"
                                strokeLinecap="round"
                                pathLength="100"
                            />
                        </svg>
                    )}
                </div>

                <NewDatabaseGraphToggle showGraph={showGraph} onChange={setShowGraph} />
            </div>

            <div className="ticketData-contentContainer">
                <NewDatabaseFilterPanel
                    isOpen={showFilterPanel}
                    onClose={handleCloseFilterPanel}
                    onFiltersChange={setActiveFilters}
                />

                <div className="ticketData-mainContent">
                    <div
                        className={`ticketData-tableContainer ${showGraph ? 'with-graph' : ''}`}
                        style={showGraph ? { '--table-flex': `${100 - graphHeight}%` } as React.CSSProperties : undefined}
                    >
                        <div className="ticketData-tableWrapper">
                            <SQLTable
                                tableName={activeTable}
                                tableData={filteredTableData}
                                idColumn={'id'}
                                columnConfigs={COL_CONFIGS[activeTable]?.columns ?? []}
                                sortOrder={COL_CONFIGS[activeTable]?.sortOrder}
                                enableSummary={true}
                                summaryConfig={COL_CONFIGS[activeTable]?.tableSummary}
                                monthNavigation={true}
                                initialMonthYear={initialMonth || undefined}
                                enableWarnings={true}
                                enableSettings={COL_CONFIGS[activeTable]?.enableSettings}
                                warningRules={COL_CONFIGS[activeTable]?.warningRules}
                                showTable={showTable}
                                initialFilter={initialFilter}
                                persistFilters={true}
                                onRowClick={handleRowClick}
                                rowColor={COL_CONFIGS[activeTable]?.rowColor}
                                userData={{ role: userRole, hasAccAccess, hasAdminAccess }}
                            />
                        </div>
                    </div>

                    {showGraph && (
                        <div className="ticketData-resizeHandle" onMouseDown={handleMouseDown}>
                            <div className="ticketData-resizeHandle-bar" />
                        </div>
                    )}

                    <div
                        className={`ticketData-graphContainer ${showGraph ? 'visible' : ''}`}
                        style={showGraph ? { '--graph-flex': `${graphHeight}%` } as React.CSSProperties : undefined}
                    >
                        <NewDatabaseGraphPanel
                            activeTable={activeTable}
                            tableData={filteredTableData}
                            isTransitioning={isTransitioning}
                        />
                    </div>
                </div>

                <NewDatabaseRowModal
                    isOpen={showRowModal}
                    onClose={handleCloseRowModal}
                    rowData={selectedRowData}
                />
            </div>
        </>
    );
};

export default CrudeMode;
